import type { IGetLatestPaymentInfoResponse } from '@/shared/api/billing/types';
import type { BeginCheckoutEventData } from '@/shared/thirdParty/gtm/types';
import type { User } from '@/shared/types/User';

import { IS_WHITE_LABEL } from '@/shared/environments';

const MICROSOFT_ADS_SCRIPT_ID = 'microsoft-ads-uet';
const OPENAI_ADS_SCRIPT_ID = 'openai-ads-pixel';
const COOKIEBOT_CONSENT_EVENTS = ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline'] as const;

const MICROSOFT_ADS_UET_ID = import.meta.env.VITE_MICROSOFT_ADS_UET_ID;
const OPENAI_ADS_PIXEL_ID = import.meta.env.VITE_OPENAI_ADS_PIXEL_ID;

const ADS_EVENT_NAMES = {
  beginCheckout: 'begin_checkout',
  purchase: 'purchase',
  signUp: 'sign_up',
  trialPreviewViewed: 'trial_preview_viewed',
} as const;

type AdsEventName = (typeof ADS_EVENT_NAMES)[keyof typeof ADS_EVENT_NAMES];

const OPENAI_ADS_EVENTS: Record<
  AdsEventName,
  {
    eventName: 'checkout_started' | 'contents_viewed' | 'order_created' | 'registration_completed';
    type: 'contents' | 'customer_action';
  }
> = {
  [ADS_EVENT_NAMES.beginCheckout]: { eventName: 'checkout_started', type: 'contents' },
  [ADS_EVENT_NAMES.purchase]: { eventName: 'order_created', type: 'contents' },
  [ADS_EVENT_NAMES.signUp]: { eventName: 'registration_completed', type: 'customer_action' },
  [ADS_EVENT_NAMES.trialPreviewViewed]: { eventName: 'contents_viewed', type: 'contents' },
};

type AdsTrackingConfig = {
  enabled?: boolean;
  microsoftUetId?: string;
  openAiPixelId?: string;
};

type RevenueData = {
  currency: string;
  value: number;
};

let removeCookiebotConsentListeners: (() => void) | undefined;

const appendAsyncScript = ({ id, src, onLoad }: { id: string; src: string; onLoad?: () => void }) => {
  const script = document.createElement('script');

  script.async = true;
  script.id = id;
  script.src = src;
  if (onLoad) script.onload = onLoad;

  document.head.appendChild(script);
};

const initMicrosoftAds = (uetId: string) => {
  if (document.getElementById(MICROSOFT_ADS_SCRIPT_ID)) return;

  const queue = (window.uetq || []) as NonNullable<Window['uetq']>;
  window.uetq = queue;
  queue.push('consent', 'default', { ad_storage: 'denied' });

  appendAsyncScript({
    id: MICROSOFT_ADS_SCRIPT_ID,
    src: `https://bat.bing.com/bat.js?ti=${uetId}`,
    onLoad: () => {
      if (!window.UET) return;

      const queuedEvents = window.uetq;
      window.uetq = new window.UET({
        enableAutoSpaTracking: true,
        q: queuedEvents,
        ti: uetId,
        ts: Date.now(),
      });
      window.uetq.push('pageLoad');
    },
  });
};

const hasMarketingConsent = () => window.Cookiebot?.consent?.marketing === true;

const updateMicrosoftAdsConsent = () => {
  window.uetq?.push('consent', 'update', {
    ad_storage: hasMarketingConsent() ? 'granted' : 'denied',
  });
};

const initOpenAiAds = (pixelId: string) => {
  if (document.getElementById(OPENAI_ADS_SCRIPT_ID)) return;

  const createQueue = (): NonNullable<Window['oaiq']> => {
    const queue = ((...args: unknown[]) => {
      queue.q = queue.q || [];
      queue.q.push(args);
    }) as NonNullable<Window['oaiq']>;

    return queue;
  };

  const queue = window.oaiq || createQueue();
  window.oaiq = queue;
  queue('init', { pixelId });

  appendAsyncScript({
    id: OPENAI_ADS_SCRIPT_ID,
    src: 'https://bzrcdn.openai.com/sdk/oaiq.min.js',
  });
};

const syncAdsConsent = (openAiPixelId?: string) => {
  updateMicrosoftAdsConsent();

  if (openAiPixelId && hasMarketingConsent()) {
    initOpenAiAds(openAiPixelId);
  }

  window.oaiq?.('consent', hasMarketingConsent());
};

const listenForCookiebotConsent = (openAiPixelId?: string) => {
  removeCookiebotConsentListeners?.();

  const handleConsentChange = () => syncAdsConsent(openAiPixelId);

  COOKIEBOT_CONSENT_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleConsentChange);
  });

  removeCookiebotConsentListeners = () => {
    COOKIEBOT_CONSENT_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, handleConsentChange);
    });
  };

  if (window.Cookiebot) handleConsentChange();
};

export const initAdsTracking = ({
  enabled = !IS_WHITE_LABEL,
  microsoftUetId = MICROSOFT_ADS_UET_ID,
  openAiPixelId = OPENAI_ADS_PIXEL_ID,
}: AdsTrackingConfig = {}) => {
  if (!enabled) {
    removeCookiebotConsentListeners?.();
    removeCookiebotConsentListeners = undefined;
    return;
  }

  if (microsoftUetId) initMicrosoftAds(microsoftUetId);
  listenForCookiebotConsent(openAiPixelId);
};

const trackMicrosoftAdsEvent = (eventName: string, properties: Record<string, unknown> = {}) => {
  window.uetq?.push('event', eventName, properties);
};

const trackOpenAiAdsEvent = (eventName: AdsEventName, data: Record<string, unknown> = {}, eventId?: string) => {
  if (!hasMarketingConsent()) return;

  const openAiEvent = OPENAI_ADS_EVENTS[eventName];
  const payload = { type: openAiEvent.type, ...data };

  if (eventId) {
    window.oaiq?.('measure', openAiEvent.eventName, payload, { event_id: eventId });
    return;
  }

  window.oaiq?.('measure', openAiEvent.eventName, payload);
};

const trackConversion = ({
  eventId,
  eventName,
  revenue,
}: {
  eventId?: string;
  eventName: AdsEventName;
  revenue?: RevenueData;
}) => {
  trackMicrosoftAdsEvent(eventName, {
    event_category: 'conversion',
    ...(eventId && { event_label: eventId }),
    ...(revenue && {
      currency: revenue.currency,
      revenue_value: revenue.value,
    }),
  });

  trackOpenAiAdsEvent(
    eventName,
    revenue
      ? {
          amount: Math.round(revenue.value * 100),
          currency: revenue.currency,
        }
      : {},
    eventId,
  );
};

export const AdsEvents = {
  beginCheckout: ({ ecommerce }: BeginCheckoutEventData) => {
    trackConversion({
      eventName: ADS_EVENT_NAMES.beginCheckout,
      revenue: {
        currency: ecommerce.currency,
        value: ecommerce.value,
      },
    });
  },

  purchase: ({ currency, transaction_id, value }: IGetLatestPaymentInfoResponse) => {
    trackConversion({
      eventId: transaction_id,
      eventName: ADS_EVENT_NAMES.purchase,
      revenue: { currency, value },
    });
  },

  signUp: (userId?: User['id']) => {
    trackConversion({
      eventId: userId,
      eventName: ADS_EVENT_NAMES.signUp,
    });
  },

  trialPreviewViewed: (projectId?: string) => {
    trackConversion({
      eventId: projectId,
      eventName: ADS_EVENT_NAMES.trialPreviewViewed,
    });
  },
};
